import { useEffect, useRef, useState } from "react";
import { getContactMessages } from "../../../services/crmMessages";
import { attachMessageToPlayer, detachMessageFromPlayer } from "../../../services/crmContacts";
import { ProspectTypeahead } from "../../../components/ProspectTypeahead";
import type { ProspectOption } from "../../../components/ProspectTypeahead";
import type { CrmMessageResponse } from "../../../types/crm";
import "./ContactMessageTimeline.css";

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

interface ContactMessageTimelineProps {
  contactType: string;
  contactId: string;
  channel?: string;
  refreshKey?: number;
  pendingMessages?: CrmMessageResponse[];
  missingContactMessage?: string;
}

function MessageBubble({
  msg,
  onAttachPlayer,
}: {
  msg: CrmMessageResponse;
  onAttachPlayer?: (messageId: string) => void;
}) {
  const isOutbound = msg.direction === "Outbound";
  const senderAccent =
    !isOutbound && msg.senderIdentifier
      ? `hsl(${hashString(msg.senderIdentifier) % 360}, 55%, 42%)`
      : undefined;
  const linkedPlayerNames =
    msg.linkedPlayers && msg.linkedPlayers.length > 0
      ? msg.linkedPlayers.map((p) => p.name || "player").join(", ")
      : msg.crmProspectProfileId
        ? msg.linkedPlayerName || "player"
        : null;
  const hasLink = linkedPlayerNames !== null;
  return (
    <div
      className={`message-timeline__item message-timeline__item--${msg.direction.toLowerCase()}`}
    >
      <div
        className="message-timeline__bubble"
        style={senderAccent ? { borderLeftColor: senderAccent } : undefined}
      >
        {msg.subject && (
          <div className="message-timeline__subject">{msg.subject}</div>
        )}
        {msg.body && <div className="message-timeline__body">{msg.body}</div>}
        {msg.crmMediaId && (
          <div className="message-timeline__attachment">
            <span className="message-timeline__attachment-icon">
              {msg.mediaType === "Video" ? "\u25B6" : "\uD83D\uDDBC"}
            </span>
            <span className="message-timeline__attachment-label">
              {msg.mediaType === "Video" ? "Video" : "Image"} attachment
            </span>
          </div>
        )}
        {hasLink && (
          <div className="message-timeline__player-badge">
            Linked to {linkedPlayerNames}
          </div>
        )}
      </div>
      <div
        className={`message-timeline__meta message-timeline__meta--${isOutbound ? "right" : "left"}`}
      >
        {msg.senderIdentifier && !isOutbound && (
          <span className="message-timeline__sender" style={senderAccent ? { color: senderAccent } : undefined}>
            {msg.senderIdentifier}
          </span>
        )}
        <span className="message-timeline__time">
          {new Date(msg.occurredAt).toLocaleString()}
        </span>
        {onAttachPlayer && (
          <button
            className="message-timeline__attach-btn"
            onClick={(e) => {
              e.stopPropagation();
              onAttachPlayer(msg.id);
            }}
            title={hasLink ? "Change player link" : "Attach to player"}
          >
            {hasLink ? "Re-link" : "Link to player"}
          </button>
        )}
      </div>
    </div>
  );
}

export const ContactMessageTimeline = ({
  contactType,
  contactId,
  channel,
  refreshKey,
  pendingMessages,
  missingContactMessage,
}: ContactMessageTimelineProps) => {
  const [messages, setMessages] = useState<CrmMessageResponse[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);

  const [attachingMessageId, setAttachingMessageId] = useState<string | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<ProspectOption | null>(null);
  const [attachError, setAttachError] = useState("");

  useEffect(() => {
    loadMessages(1);
  }, [contactType, contactId]);

  useEffect(() => {
    if (refreshKey !== undefined) loadMessages(1, true);
  }, [refreshKey]);

  useEffect(() => {
    const poll = async () => {
      if (document.visibilityState === "hidden") return;
      try {
        const data = await getContactMessages(contactType, contactId, 1, 20);
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          const newMsgs = data.items.filter((m) => !existingIds.has(m.id));
          return newMsgs.length > 0 ? [...prev, ...newMsgs.reverse()] : prev;
        });
      } catch {
        // silent — polling failures shouldn't disrupt the UI
      }
    };
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, [contactType, contactId]);

  const loadMessages = async (pageNumber: number, silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    setError("");
    try {
      const data = await getContactMessages(contactType, contactId, pageNumber);
      if (pageNumber === 1) {
        setMessages([...data.items].reverse());
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "instant" }), 0);
      } else {
        setMessages((prev) => [...data.items.reverse(), ...prev]);
      }
      setTotalCount(data.totalCount);
      setPage(pageNumber);
    } catch {
      setError("Failed to load messages.");
    } finally {
      setLoading(false);
    }
  };

  const handleAttachPlayer = (messageId: string) => {
    setAttachingMessageId(messageId);
    setSelectedPlayer(null);
    setAttachError("");
  };

  const confirmAttach = async () => {
    if (!attachingMessageId || !selectedPlayer) return;
    setAttachError("");
    try {
      await attachMessageToPlayer(attachingMessageId, selectedPlayer.id);
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== attachingMessageId) return m;
          const prevFkId = m.crmProspectProfileId;
          const others = (m.linkedPlayers ?? []).filter(
            (p) => p.id !== prevFkId && p.id !== selectedPlayer.id
          );
          const nextLinked = [
            ...others,
            { id: selectedPlayer.id, name: selectedPlayer.displayName },
          ];
          return {
            ...m,
            crmProspectProfileId: selectedPlayer.id,
            linkedPlayerName: nextLinked.length === 1 ? selectedPlayer.displayName : undefined,
            linkedPlayers: nextLinked,
          };
        })
      );
      setAttachingMessageId(null);
    } catch {
      setAttachError("Failed to attach message to player.");
    }
  };

  const handleDetach = async (messageId: string) => {
    try {
      await detachMessageFromPlayer(messageId);
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId) return m;
          const prevFkId = m.crmProspectProfileId;
          const nextLinked = (m.linkedPlayers ?? []).filter((p) => p.id !== prevFkId);
          return {
            ...m,
            crmProspectProfileId: undefined,
            linkedPlayerName: nextLinked.length === 1 ? nextLinked[0].name : undefined,
            linkedPlayers: nextLinked,
          };
        })
      );
    } catch {
      // silent
    }
  };

  if (loading && page === 1) {
    return <div className="message-timeline__loading">Loading messages...</div>;
  }

  if (error) {
    return <div className="message-timeline__error">{error}</div>;
  }

  const normalizeChannel = (ch: string) => {
    const lower = ch.toLowerCase();
    return lower === "twitter" ? "x" : lower;
  };
  const visibleMessages = channel
    ? messages.filter((m) => normalizeChannel(m.channel) === normalizeChannel(channel))
    : messages;

  if (visibleMessages.length === 0 && !loading) {
    return (
      <div className="message-timeline__empty">
        {missingContactMessage ? (
          <div className="message-timeline__missing-contact">{missingContactMessage}</div>
        ) : (
          <div>No messages yet.</div>
        )}
      </div>
    );
  }

  return (
    <div className="message-timeline__list">
      {messages.length < totalCount && (
        <button
          className="message-timeline__load-more"
          onClick={() => loadMessages(page + 1)}
          disabled={loading}
        >
          {loading ? "Loading..." : `Load older messages (${totalCount - messages.length} more)`}
        </button>
      )}

      {visibleMessages.map((msg) => (
        <MessageBubble key={msg.id} msg={msg} onAttachPlayer={handleAttachPlayer} />
      ))}

      {pendingMessages
        ?.filter((m) =>
          channel ? m.channel.toLowerCase() === channel.toLowerCase() : true
        )
        .map((m) => (
          <div key={m.id} style={{ opacity: 0.6 }}>
            <MessageBubble msg={m} />
          </div>
        ))}

      <div ref={bottomRef} />

      {attachingMessageId && (
        <div className="message-timeline__attach-overlay" onClick={() => setAttachingMessageId(null)}>
          <div className="message-timeline__attach-popover" onClick={(e) => e.stopPropagation()}>
            <div className="message-timeline__attach-title">Attach message to player</div>
            <ProspectTypeahead
              value={selectedPlayer}
              onSelect={setSelectedPlayer}
              placeholder="Search players..."
            />
            {attachError && <div className="message-timeline__attach-error">{attachError}</div>}
            <div className="message-timeline__attach-actions">
              <button
                className="message-timeline__attach-cancel"
                onClick={() => setAttachingMessageId(null)}
              >
                Cancel
              </button>
              {messages.find((m) => m.id === attachingMessageId)?.crmProspectProfileId && (
                <button
                  className="message-timeline__attach-detach"
                  onClick={() => {
                    handleDetach(attachingMessageId);
                    setAttachingMessageId(null);
                  }}
                >
                  Unlink
                </button>
              )}
              <button
                className="message-timeline__attach-confirm"
                disabled={!selectedPlayer}
                onClick={confirmAttach}
              >
                Attach
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
